'use client';

import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '@/config/theme';
import { useTheme } from '@/context/ThemeContext';

// Extend HTMLDivElement to include fullscreen methods
interface FullscreenElement extends HTMLDivElement {
  webkitRequestFullscreen?: () => void;
  mozRequestFullScreen?: () => void;
  msRequestFullscreen?: () => void;
}

// Extend Document to include fullscreen methods
interface FullscreenDocument extends Document {
  webkitExitFullscreen?: () => void;
  mozCancelFullScreen?: () => void;
  msExitFullscreen?: () => void;
  webkitFullscreenElement?: Element;
  mozFullScreenElement?: Element;
  msFullscreenElement?: Element;
  webkitFullscreenEnabled?: boolean;
  mozFullScreenEnabled?: boolean;
  msFullscreenEnabled?: boolean;
}
import MenuIcon from '@mui/icons-material/Menu';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import TableViewIcon from '@mui/icons-material/TableView';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CloseIcon from '@mui/icons-material/Close';

const MenuContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const HamburgerButton = styled.button<{ $isDark?: boolean }>`
  background: ${({ $isDark }) => $isDark ? theme.colors.dark.background.primary : theme.colors.white};
  border: 1px solid ${({ $isDark }) => $isDark ? theme.colors.dark.border.primary : theme.colors.gray[300]};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.sm};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  svg {
    width: 20px;
    height: 20px;
    color: ${({ $isDark }) => $isDark ? theme.colors.dark.text.primary : theme.colors.gray[600]};
  }

  &:hover {
    background: ${({ $isDark }) => $isDark ? theme.colors.dark.background.secondary : theme.colors.gray[50]};
    border-color: ${({ $isDark }) => $isDark ? theme.colors.dark.border.primary : theme.colors.gray[400]};
    transform: translateY(-1px);
    box-shadow: 0 2px 8px ${({ $isDark }) => $isDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.1)'};
  }

  &:active {
    transform: translateY(0);
  }
`;

const DropdownMenu = styled.div<{ $isOpen: boolean; $isDark?: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  background: ${({ $isDark }) => $isDark ? theme.colors.dark.background.secondary : theme.colors.white};
  border: 1px solid ${({ $isDark }) => $isDark ? theme.colors.dark.border.primary : theme.colors.gray[200]};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.xl};
  min-width: 200px;
  z-index: 1000;
  overflow: hidden;
  
  opacity: ${({ $isOpen }) => $isOpen ? 1 : 0};
  visibility: ${({ $isOpen }) => $isOpen ? 'visible' : 'hidden'};
  transform: translateY(${({ $isOpen }) => $isOpen ? '0' : '-10px'});
  transition: all 0.2s ease;
`;

const IconMenuItem = styled.div<{ $isDark?: boolean }>`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  cursor: pointer;
  transition: background-color 0.2s ease;
  color: ${({ $isDark }) => $isDark ? theme.colors.dark.text.primary : theme.colors.gray[700]};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.medium};
  background: ${({ $isDark }) => $isDark ? theme.colors.dark.background.primary : theme.colors.white};

  svg {
    font-size: 18px;
    margin-right: ${theme.spacing.sm};
  }

  &:hover {
    background: ${({ $isDark }) => $isDark ? theme.colors.dark.background.tertiary : theme.colors.gray[50]};
  }

  &:first-child {
    border-top-left-radius: ${theme.borderRadius.lg};
    border-top-right-radius: ${theme.borderRadius.lg};
  }

  &:last-child {
    border-bottom-left-radius: ${theme.borderRadius.lg};
    border-bottom-right-radius: ${theme.borderRadius.lg};
  }
`;


const TableModal = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: ${({ $isOpen }) => $isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: ${theme.spacing.md};
  
  @media (max-width: 768px) {
    padding: ${theme.spacing.sm};
  }
`;

const TableContent = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  width: 95vw;
  max-width: 1200px;
  height: 90vh;
  max-height: 800px;
  display: flex;
  flex-direction: column;
  position: relative;
  
  @media (max-width: 768px) {
    width: 98vw;
    height: 95vh;
  }
`;

const TableHeader = styled.div`
  padding: ${theme.spacing.xl};
  border-bottom: 1px solid ${theme.colors.gray[200]};
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h2 {
    margin: 0;
    font-size: ${theme.fontSizes.xl};
    color: ${theme.colors.gray[800]};
  }
  
  @media (max-width: 768px) {
    padding: ${theme.spacing.lg};
    
    h2 {
      font-size: ${theme.fontSizes.lg};
    }
  }
`;

const CloseButton = styled.button<{ $isDark?: boolean }>`
  background: ${({ $isDark }) => $isDark ? theme.colors.dark.background.secondary : theme.colors.gray[100]};
  border: 1px solid ${({ $isDark }) => $isDark ? theme.colors.dark.border.primary : theme.colors.gray[300]};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${({ $isDark }) => $isDark ? theme.colors.dark.text.primary : theme.colors.gray[700]};
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.medium};

  &:hover {
    background: ${({ $isDark }) => $isDark ? theme.colors.dark.background.tertiary : theme.colors.gray[200]};
  }

  svg {
    width: 16px;
    height: 16px;
  }
  
  @media (max-width: 768px) {
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
    font-size: ${theme.fontSizes.xs};
    
    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

const TableContainer = styled.div`
  flex: 1;
  overflow: auto;
  padding: 0 ${theme.spacing.xl};
  padding-bottom: ${theme.spacing.xl};
  
  @media (max-width: 768px) {
    padding: 0 ${theme.spacing.lg};
    padding-bottom: ${theme.spacing.lg};
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 600px;
  
  th, td {
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    text-align: left;
    border-bottom: 1px solid ${theme.colors.gray[200]};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
  }
  
  th {
    background: ${theme.colors.gray[50]};
    font-weight: ${theme.fontWeights.semibold};
    color: ${theme.colors.gray[700]};
    position: sticky;
    top: 0;
    z-index: 1;
  }
  
  tbody tr:hover {
    background: ${theme.colors.gray[50]};
  }
  
  @media (max-width: 768px) {
    min-width: 500px;
    
    th, td {
      padding: ${theme.spacing.xs} ${theme.spacing.sm};
      font-size: ${theme.fontSizes.sm};
      max-width: 150px;
    }
  }
`;

export interface ChartHamburgerMenuProps {
  chartTitle: React.ReactNode|string;
  tableData?: Array<Record<string, string | number>>;
  chartContainerRef: React.RefObject<HTMLDivElement | null>;
  fullscreenEnabled?: boolean;
  onZoomToggle?: (enabled: boolean) => void;
  initialZoomEnabled?: boolean;
  totalChecks?: number;
  period?: string;
  /** Hide the Fullscreen menu item */
  hideFullscreen?: boolean;
  /** Hide the Table View menu item */
  hideTableView?: boolean;
  /** Show table data in table view modal (default: false) */
  showTableData?: boolean;
}

export const ChartHamburgerMenu: React.FC<ChartHamburgerMenuProps> = ({
  chartTitle,
  tableData,
  chartContainerRef,
  fullscreenEnabled: propFullscreenEnabled,
  onZoomToggle,
  initialZoomEnabled = false,
  totalChecks,
  period,
  hideFullscreen = false,
  hideTableView = false,
  showTableData = false,
}) => {
  const { isDark } = useTheme();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isTableViewOpen, setIsTableViewOpen] = useState(false);
  const [zoomEnabled, setZoomEnabled] = useState(initialZoomEnabled);
  const [fullscreenEnabled, setFullscreenEnabled] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Check if fullscreen is supported (only on client side)
  useEffect(() => {
    if (typeof window !== 'undefined' && propFullscreenEnabled !== false) {
      const isSupported = !!(
        document.fullscreenEnabled ||
        (document as FullscreenDocument).webkitFullscreenEnabled ||
        (document as FullscreenDocument).mozFullScreenEnabled ||
        (document as FullscreenDocument).msFullscreenEnabled
      );
      setFullscreenEnabled(isSupported);
    }
  }, [propFullscreenEnabled]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!menuRef.current) return;
      
      const target = event.target as Node;
      if (!menuRef.current.contains(target)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isMenuOpen]);

  // Handle fullscreen state changes
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleFullscreenChange = () => {
      const doc = document as FullscreenDocument;
      const isCurrentlyFullscreen = !!(
        doc.fullscreenElement ||
        doc.webkitFullscreenElement ||
        doc.mozFullScreenElement ||
        doc.msFullscreenElement
      );
      setIsFullscreen(isCurrentlyFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleFullscreen = () => {
    if (!chartContainerRef.current || typeof window === 'undefined') return;

    if (!isFullscreen) {
      const element = chartContainerRef.current as FullscreenElement;
      if (element.requestFullscreen) {
        element.requestFullscreen();
      } else if (element.webkitRequestFullscreen) {
        element.webkitRequestFullscreen();
      } else if (element.mozRequestFullScreen) {
        element.mozRequestFullScreen();
      } else if (element.msRequestFullscreen) {
        element.msRequestFullscreen();
      }
    } else {
      const doc = document as FullscreenDocument;
      if (doc.exitFullscreen) {
        doc.exitFullscreen();
      } else if (doc.webkitExitFullscreen) {
        doc.webkitExitFullscreen();
      } else if (doc.mozCancelFullScreen) {
        doc.mozCancelFullScreen();
      } else if (doc.msExitFullscreen) {
        doc.msExitFullscreen();
      }
    }
    setIsMenuOpen(false);
  };

  const handleTableView = () => {
    setIsTableViewOpen(true);
    setIsMenuOpen(false);
  };


  const handleZoomToggle = () => {
    const newState = !zoomEnabled;
    setZoomEnabled(newState);
    onZoomToggle?.(newState);
    setIsMenuOpen(false);
  };

  const hasMenuItems = (fullscreenEnabled && !hideFullscreen) || (!hideTableView && showTableData);
  if (!hasMenuItems) {
    return null;
  }

  return (
    <>
      <MenuContainer ref={menuRef}>
        <HamburgerButton onClick={toggleMenu} $isDark={isDark} aria-haspopup="menu" aria-expanded={isMenuOpen ? 'true' : 'false'}>
          <MenuIcon />
        </HamburgerButton>

        <DropdownMenu 
          $isOpen={isMenuOpen} 
          $isDark={isDark} 
          role="menu"
        >
          {fullscreenEnabled && !hideFullscreen && (
            <IconMenuItem onClick={handleFullscreen} $isDark={isDark} role="menuitem">
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
            </IconMenuItem>
          )}
          {!hideTableView && showTableData && (
            <IconMenuItem onClick={handleTableView} $isDark={isDark} role="menuitem">
              <TableViewIcon />
              Table View
            </IconMenuItem>
          )}
          {/* <IconMenuItem onClick={handleZoomToggle} $isDark={isDark} role="menuitem">
            {zoomEnabled ? <ZoomOutIcon /> : <ZoomInIcon />}
            {zoomEnabled ? 'Disable Zoom/Drag' : 'Enable Zoom/Drag'}
          </IconMenuItem> */}
        </DropdownMenu>
      </MenuContainer>

      {/* Table Modal */}
      {showTableData && (
        <TableModal $isOpen={isTableViewOpen}>
          <TableContent>
            <TableHeader>
              <h2>
                {chartTitle} - Data Table
                {totalChecks !== undefined && period 
                  ? ` (${totalChecks} checks, ${period})`
                  : totalChecks !== undefined 
                  ? ` (${totalChecks} checks)`
                  : period 
                  ? ` (${period})`
                  : ''}
              </h2>
              <CloseButton onClick={() => setIsTableViewOpen(false)} $isDark={isDark}>
                <CloseIcon />
                Close
              </CloseButton>
            </TableHeader>
            <TableContainer>
              {tableData && tableData.length > 0 && (
                <Table>
                  <thead>
                    <tr>
                      {Object.keys(tableData[0]).map((key) => (
                        <th key={key}>{key.charAt(0).toUpperCase() + key.slice(1)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.map((row, index) => (
                      <tr key={index}>
                        {Object.values(row).map((value, i) => (
                          <td key={i} title={String(value)}>{String(value)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
              {(!tableData || tableData.length === 0) && (
                <div style={{ 
                  padding: theme.spacing.xl, 
                  textAlign: 'center', 
                  color: theme.colors.gray[500] 
                }}>
                  No data available
                </div>
              )}
            </TableContainer>
          </TableContent>
        </TableModal>
      )}
    </>
  );
};